import { t } from '@lingui/core/macro'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { EventData } from 'features/systemLens'
import { useDesignSystemTheme } from 'features/theme/DesignSystemThemeProvider'
import { TimeSeriesService, useScatterplotContext } from 'features/timeSeries'
import {
  agentBandOverlay,
  useAgentChartOverlay,
} from 'features/timeSeries/agentTab/agentChartOverlay'
import {
  droppedRanges,
  getHideExcludedDays,
  modelPenIds,
  subscribeHideExcluded,
  ungradedHoles,
} from 'features/timeSeries/agentTab/modelSeries'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import { useExperimentChartOverlays } from 'features/timeSeries/experiment/ExperimentLabContext'
import { removedPointsDigest } from 'features/timeSeries/experiment/overlays'
import { useSentinelCases } from 'features/timeSeries/hooks/useSentinelCases'
import {
  buildSentinelBandOverlays,
  SentinelBandOverlay,
  sentinelOverlayValuesDigest,
  useSentinelScores,
} from 'features/timeSeries/hooks/useSentinelScores'
import {
  isPerfEnabled,
  perfMark,
  perfRenderPrep,
} from 'features/timeSeries/perf/processDataPerf'
import {
  useSentinelStudioChartOverlays,
  useSentinelStudioFocusCase,
} from 'features/timeSeries/sentinelStudio/SentinelStudioContext'
import { logAnnotationDebug } from 'features/timeSeries/utils/annotationDebug'
import { collectPenCompanions } from 'features/timeSeries/utils/penCompanions'
import { estimateSampleIntervalMs } from 'features/timeSeries/utils/sampleCadence'
import {
  clampTimeRange,
  minSpanForSampleIntervalMs,
} from 'features/timeSeries/utils/timeRangeClamp'
import {
  buildUPlotScaleRanges,
  generateUPlotOptions,
} from 'features/timeSeries/utils/uPlotConfig'
import {
  generateUPlotData,
  UPlotSeriesConfig,
} from 'features/timeSeries/utils/uPlotData'
import {
  eventMarkersPlugin,
  excludedDataPlugin,
  experimentMarkersPlugin,
  sentinelBandsPlugin,
  sentinelCasesPlugin,
  thresholdsPlugin,
  tooltipPlugin,
} from 'features/timeSeries/utils/uPlotPlugins'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react'
import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import { SEARCH_PARAM_CASE_ID } from 'routes/cases/-searchParams'
import { EnhancedSensorEntry } from 'types'
import uPlot from 'uplot'

interface ThresholdValue {
  value: number
  type: 'lowerLower' | 'lower' | 'upper' | 'upperUpper'
  sensors: string[]
}

// align: 1 holds each value forward until the next one: a bucket's from its
// start, which is the timestamp the aggregate endpoint labels it with, and a
// stepped pen's sample until the next sample
const STEPPED_BUCKET_PATHS = uPlot.paths?.stepped?.({ align: 1 })

export interface UPlotSeriesAppearance {
  alpha?: number
  width?: number
  dash?: number[]
  pointsShow?: boolean
  pointsSize?: number
}

interface UseUPlotGeneratorsProps {
  operationalEvents: EventData[]
  enhancedSensorEntries: EnhancedSensorEntry[]
  uniqueThresholds: ThresholdValue[]
  allSensorsToRender: Array<{ id: string; name: string }>
  width: number
  height: number
  annotationProtectedSensorIds?: string[]
  highlightedSensorIdOverride?: string | null
  onCursorMove?: (idx: number | null, u: uPlot) => void
  onCursorLeave?: () => void
  secondaryHighlightedSensorIdsOverride?: string[]
}

/**
 * The *shape* of a scale's range, with the numbers deliberately left out.
 *
 * Which end is pinned and which is left to uPlot's autoscaling is structural:
 * `[min, null]` and `[min, max]` are configured differently and behave
 * differently. The actual limits are not — moving an axis from [0, 100] to
 * [0, 100.5] is the same chart showing a slightly different window, and is
 * applied to the live instance with `setScale`. Serializing the numbers here is
 * what used to make every range change destroy and rebuild the chart.
 */
function describeScaleRangeShape(
  range: uPlot.Scale['range']
): string[] | string | null {
  if (Array.isArray(range)) {
    return range.map((value) =>
      value === null || value === undefined ? 'auto' : 'fixed'
    )
  }

  return range ? 'dynamic' : null
}

function buildUPlotScaleSignature(scales: uPlot.Scales): string {
  return JSON.stringify(
    Object.entries(scales)
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      .map(([scaleKey, scale]) => [
        scaleKey,
        {
          auto: scale.auto ?? null,
          distr: scale.distr ?? null,
          time: scale.time ?? null,
          range: describeScaleRangeShape(scale.range),
        },
      ])
  )
}

function buildUPlotAxisSignature(axes: uPlot.Axis[]): string {
  return JSON.stringify(
    axes.map((axis) => ({
      gap: axis.gap ?? null,
      gridShow: axis.grid?.show ?? null,
      label: axis.label ?? null,
      scale: axis.scale ?? null,
      show: axis.show ?? null,
      side: axis.side ?? null,
      size: axis.size ?? null,
      space: axis.space ?? null,
      stroke: axis.stroke ?? null,
      ticksShow: axis.ticks?.show ?? null,
    }))
  )
}

export function buildUPlotOptionsStructuralSignature(
  options: uPlot.Options
): string {
  const series = (options.series ?? []).map((series) => {
    const config = series as UPlotSeriesConfig

    return {
      isRegression: config._isRegression ?? null,
      label:
        typeof config.label === 'string'
          ? config.label
          : config.label
            ? t`[non-string-label]`
            : null,
      scale: config.scale ?? null,
      sensorId: config._sensorId ?? null,
      show: config.show ?? null,
      // Gap state is structural: `gaps` is a CLOSURE over the removed spans
      // and `spanGaps` decides whether uPlot consults it at all — neither can
      // be applied by setData, so a change must recreate the series.
      spanGaps: config.spanGaps ?? null,
      excludedDigest: config._excludedDigest ?? null,
      stepped: config._stepped ?? null,
      stroke: config.stroke ?? null,
    }
  })
  const plugins = (options.plugins ?? []).map((plugin) =>
    Object.keys(plugin.hooks ?? {}).sort()
  )

  return JSON.stringify({
    axes: buildUPlotAxisSignature(options.axes ?? []),
    ms: options.ms ?? null,
    plugins,
    scales: buildUPlotScaleSignature(options.scales ?? {}),
    series,
  })
}

const modelPenCutRanges = (): Map<string, Array<[number, number]>> => {
  const holes = ungradedHoles()
  return new Map(
    holes.length ? modelPenIds().map((id) => [id, holes] as const) : []
  )
}

export const useUPlotGenerators = ({
  operationalEvents,
  enhancedSensorEntries,
  uniqueThresholds,
  allSensorsToRender,
  width,
  height,
  annotationProtectedSensorIds,
  highlightedSensorIdOverride,
  onCursorMove,
  onCursorLeave,
  secondaryHighlightedSensorIdsOverride,
}: UseUPlotGeneratorsProps) => {
  const {
    siteID,
    timeRange,
    activeSentinels,
    chartData,
    derivedValues,
    regressionConfigs,
    regressionResults,
    unitMapping,
    sensorRanges,
    singleScaleMode,
    connectPoints,
    xAxisSensorId,
    customAxisRanges,
    customXAxisRange,
    sensorAxisMapping,
    dataLayers,
    sensorVisibilityConfig,
    sentinelVisibilityConfig,
    customSensorColors,
    highlightedSensorId,
    secondaryHighlightedSensorIds,
    deemphasizedSensorIds,
    selectedSensors,
    filteredPoints,
    smoothingConfigs,
    resampleConfigs,
    computedScalePairs,
    setTimeRange,
    setTimeXAxisPreviewRange,
    exitLiveTail,
    loadTriggerRef,
  } = useScatterplotContext()
  const { theme, resolvedTheme } = useDesignSystemTheme()

  // Pen count for the zoom handler's interval computation, held in a ref for
  // the same reason as minZoomSpanRef below: handleZoom must keep a stable
  // identity, but the interval it bakes into the zoomed range must share the
  // bucket budget across the pens on the chart at zoom time.
  const penCountRef = useRef(selectedSensors.length)
  useEffect(() => {
    penCountRef.current = selectedSensors.length
  }, [selectedSensors.length])

  const effectiveHighlightedSensorId =
    highlightedSensorIdOverride ?? highlightedSensorId
  const effectiveSecondaryHighlightedSensorIds =
    secondaryHighlightedSensorIdsOverride ?? secondaryHighlightedSensorIds

  const penCompanions = useMemo(
    () =>
      collectPenCompanions({
        penIds: allSensorsToRender,
        smoothedSensorIds: Object.values(smoothingConfigs)
          .filter(
            (config) =>
              config.enabled && (config.display ?? 'replace') === 'overlay'
          )
          .map((config) => config.sensorId),
        bucketedSensorIds: Object.values(resampleConfigs)
          .filter((config) => config.enabled)
          .map((config) => config.sensorId),
      }),
    [allSensorsToRender, smoothingConfigs, resampleConfigs]
  )

  // Experiment Lab overlays (EXPERIMENT sidebar tab): removed-point markers
  // from the data preview and the trained expected/residual band. The band
  // reuses the sentinel band overlay shape/plugin; its color follows the
  // target pen, exactly like sentinel bands.
  const {
    bandOverlay: experimentBandOverlay,
    removedPoints: experimentRemovedPoints,
    outOfBandPoints: experimentOutOfBandPoints,
    testBoundaryMs: experimentTestBoundaryMs,
    showTargetBounds,
    showOutOfBoundsFlags,
    showResidual,
  } = useExperimentChartOverlays()

  // Sentinel builder's live plan preview: split regions + points its cleaning
  // plan would drop. One call, reused below for the planned split overlay.
  const studioOverlays = useSentinelStudioChartOverlays()
  const agentOverlay = useAgentChartOverlay()
  const studioRemovedPoints = studioOverlays.removedPoints

  // Pen filters share the exclusion treatment with the experiment's cleaning
  // removals AND the sentinel builder's live plan-preview removals: a break in
  // the line, plus a lane along the chart bottom.
  const excludedPoints = useMemo(
    () => [
      ...experimentRemovedPoints,
      ...studioRemovedPoints,
      ...filteredPoints,
    ],
    [experimentRemovedPoints, studioRemovedPoints, filteredPoints]
  )

  // "Hide rows the model filtered out". Subscribed rather than read once, so the
  // chart re-renders when the toggle moves.
  const hideExcluded = useSyncExternalStore(
    subscribeHideExcluded,
    getHideExcludedDays,
    getHideExcludedDays
  )

  const excludedBySensor = useMemo(() => {
    const xsBySensor = new Map<string, number[]>()
    excludedPoints.forEach(({ sensorId, xs }) => {
      const existing = xsBySensor.get(sensorId)
      if (existing) existing.push(...xs)
      else xsBySensor.set(sensorId, [...xs])
    })
    xsBySensor.forEach((xs) => xs.sort((left, right) => left - right))
    return xsBySensor
  }, [excludedPoints])

  const uPlotDataResult = useMemo(() => {
    // generateUPlotData (and the resample inside it) is the O(rows x pens)
    // step the fetch records' mergeMs stops short of -- it runs here, after
    // firstDataReady. Measured where it runs; inert unless the perf flag is on.
    const renderPrepStart = isPerfEnabled() ? performance.now() : null
    const result = generateUPlotData({
      dataLayers,
      chartData,
      allSensorsToRender,
      enhancedSensorEntries,
      unitMapping,
      sensorAxisMapping,
      regressionConfigs,
      regressionResults,
      derivedValues,
      xAxisSensorId,
      connectPoints,
      sensorVisibilityConfig,
      customSensorColors,
      highlightedSensorId: effectiveHighlightedSensorId,
      secondaryHighlightedSensorIds: effectiveSecondaryHighlightedSensorIds,
      annotationProtectedSensorIds,
      deemphasizedSensorIds,
      singleScaleMode,
      penCompanions,
      computedScalePairs,
      steppedPaths: STEPPED_BUCKET_PATHS,
      excludedBySensor,
      // Periods an agent model's gate or masked ranges threw out.
      //
      // Cut as RANGES, not as a list of removed samples: the rows are already
      // blank in chartData, but the series builder skips a blank row instead of
      // emitting a null, so the path runs straight from the last kept point to
      // the first one after — and a pixel-wide gap per removed sample leaves
      // nearly all of that bridge on screen. Cutting the whole period is what
      // makes it look gone.
      //
      // Every pen, not just the model's: the filter answers "show me only what
      // the fit looked at", and a raw tag drawn straight through a gated-out
      // shutdown is the comparison that makes a step look real.
      droppedRanges: hideExcluded ? droppedRanges() : [],
      // The model's own pens break across holes it never graded, matching
      // the band; raw tags keep drawing through them since their data exists.
      droppedRangesBySensor: modelPenCutRanges(),
    })
    if (renderPrepStart !== null) {
      perfRenderPrep(performance.now() - renderPrepStart)
      // One-shot milestone to line up against firstDataReady, matching the
      // existing mark pattern; the accumulator above records every rerun.
      perfMark('firstRenderPrepDone')
    }
    return result
  }, [
    dataLayers,
    chartData,
    allSensorsToRender,
    enhancedSensorEntries,
    unitMapping,
    computedScalePairs,
    sensorAxisMapping,
    regressionConfigs,
    regressionResults,
    derivedValues,
    xAxisSensorId,
    connectPoints,
    sensorVisibilityConfig,
    customSensorColors,
    effectiveHighlightedSensorId,
    effectiveSecondaryHighlightedSensorIds,
    annotationProtectedSensorIds,
    deemphasizedSensorIds,
    singleScaleMode,
    penCompanions,
    excludedBySensor,
    hideExcluded,
  ])

  // Store cursor callbacks in refs so the tooltip plugin instance (and therefore
  // the plugins array and uPlotOptions) stays referentially stable when only
  // the callback identity changes. This prevents chart destruction/recreation
  // during drag-to-zoom caused by React state updates triggering re-renders.
  const onCursorMoveRef = useRef(onCursorMove)
  const onCursorLeaveRef = useRef(onCursorLeave)
  useEffect(() => {
    onCursorMoveRef.current = onCursorMove
  }, [onCursorMove])
  useEffect(() => {
    onCursorLeaveRef.current = onCursorLeave
  }, [onCursorLeave])

  const thresholdsEnabled =
    dataLayers.find((layer) => layer.id === 'thresholds')?.enabled ?? false
  const eventsEnabled =
    dataLayers.find((layer) => layer.id === 'events')?.enabled ?? false
  const sentinelsEnabled =
    dataLayers.find((layer) => layer.id === 'sentinels')?.enabled ?? false

  const { scores: sentinelScores } = useSentinelScores(
    siteID,
    activeSentinels,
    timeRange,
    { enabled: sentinelsEnabled }
  )
  const sentinelOverlays = useMemo(
    () =>
      sentinelsEnabled
        ? buildSentinelBandOverlays({
            // Individually-hidden sentinels stay active (their drawer row is
            // the way back) but draw nothing.
            activeSentinels: activeSentinels.filter(
              (sentinel) => sentinelVisibilityConfig[sentinel.entryId] !== false
            ),
            scores: sentinelScores,
            allSensorsToRender,
            customSensorColors,
          })
        : [],
    [
      sentinelsEnabled,
      activeSentinels,
      sentinelVisibilityConfig,
      sentinelScores,
      allSensorsToRender,
      customSensorColors,
    ]
  )

  // Cases opened for the sentinels on the chart, as bubbles at their breach
  // (time, actual). Hidden with their sentinel's band, and only for sentinels
  // still on the chart.
  const focusCaseBubble = useSentinelStudioFocusCase()
  const navigate = useNavigate()
  const handleCaseBubbleClick = useCallback(
    (sentinelEntryId: string, caseId: string) => {
      if (caseId.startsWith('md-case-')) {
        void navigate({
          to: '/cases/details',
          search: {
            [SEARCH_PARAM_SITE_ID]: siteID,
            [SEARCH_PARAM_CASE_ID]: caseId,
          },
        })
        return
      }
      focusCaseBubble(sentinelEntryId, caseId)
    },
    [navigate, siteID, focusCaseBubble]
  )
  const { cases: allSentinelCases } = useSentinelCases(
    siteID,
    activeSentinels,
    {
      enabled: sentinelsEnabled,
    }
  )
  const sentinelCases = useMemo(() => {
    if (!sentinelsEnabled) return []
    return allSentinelCases.filter(
      (kase) => sentinelVisibilityConfig[kase.sentinelEntryId] !== false
    )
  }, [sentinelsEnabled, allSentinelCases, sentinelVisibilityConfig])

  // Planned split preview: while designing, shade the draft plan's train/test
  // regions on the chart (the honest split the served band can't show). A
  // band-less overlay carrying only splitRanges — drawSplitRegions shades them,
  // the band-drawing loops skip it (empty series).
  const plannedSplit = studioOverlays
  const plannedOverlays = useMemo((): SentinelBandOverlay[] => {
    if (!plannedSplit.splitRanges.length || !plannedSplit.target) return []
    return [
      {
        targetSensorId: plannedSplit.target,
        sentinelName: '',
        method: '',
        label: '',
        color: '#94a3b8',
        xs: [],
        upper: [],
        lower: [],
        center: [],
        actual: [],
        sigma: [],
        trainedSplitMs: plannedSplit.testBoundaryMs,
        modelCreatedMs: null,
        windowStartMs: null,
        splitRanges: plannedSplit.splitRanges,
      },
    ]
  }, [plannedSplit])

  // An agent model opened in the Agent tab shades its BASELINE window (and the
  // graded span after it) on this chart — the same band-less splitRanges path
  // the planned split uses, so the reference period is visible against the
  // real data instead of only inside the chat.
  const agentModelOverlays = useMemo((): SentinelBandOverlay[] => {
    const overlay = agentBandOverlay(agentOverlay)
    return overlay ? [overlay] : []
  }, [agentOverlay])

  const experimentOverlays = useMemo((): SentinelBandOverlay[] => {
    if (!experimentBandOverlay) return []
    const penIndex = allSensorsToRender.findIndex(
      (sensor) => sensor.id === experimentBandOverlay.targetSensorId
    )
    const color =
      customSensorColors?.[experimentBandOverlay.targetSensorId] ??
      getSensorColor(penIndex >= 0 ? penIndex : 0)
    return [{ ...experimentBandOverlay, color }]
  }, [experimentBandOverlay, allSensorsToRender, customSensorColors])
  const experimentOutOfBand = useMemo(
    () =>
      showOutOfBoundsFlags
        ? {
            xs: experimentOutOfBandPoints.map((point) => point.x),
            values: experimentOutOfBandPoints.map((point) => point.actual),
          }
        : { xs: [] as number[], values: [] as number[] },
    [experimentOutOfBandPoints, showOutOfBoundsFlags]
  )

  const penColors = useMemo(() => {
    const colors: Record<string, string> = {}
    allSensorsToRender.forEach((sensor, index) => {
      colors[sensor.id] =
        customSensorColors?.[sensor.id] ?? getSensorColor(index)
    })
    return colors
  }, [allSensorsToRender, customSensorColors])

  const pluginStructuralSignature = useMemo(
    () =>
      JSON.stringify({
        events: eventsEnabled
          ? operationalEvents.map((event) => ({
              description: event.description,
              priority: event.metadata?.priority ?? null,
              status: event.metadata?.status ?? null,
              timestamp: event.timestamp,
              type: event.type,
            }))
          : [],
        eventsEnabled,
        sensorAxisMapping,
        // The plugin instance closes over the band series, so the chart must
        // recreate when they change. The raw arrays would bloat the
        // signature, so digest them: window + method + length, plus a values
        // checksum — endpoints and count alone can collide across responses
        // whose interior values differ (same surviving buckets, new scores),
        // which would leave a stale plugin closure drawing the old band.
        sentinelOverlays: sentinelOverlays.map((overlay) => ({
          color: overlay.color,
          firstX: overlay.xs[0] ?? null,
          label: overlay.label,
          lastX: overlay.xs[overlay.xs.length - 1] ?? null,
          method: overlay.method,
          points: overlay.xs.length,
          target: overlay.targetSensorId,
          valuesDigest: sentinelOverlayValuesDigest(overlay),
        })),
        sentinelsEnabled,
        // The planned split overlay closes over its ranges, so a changed draft
        // split must recreate the chart.
        plannedSplit: plannedOverlays[0]?.splitRanges ?? [],
        // Same for the agent model's baseline shading.
        agentSplit: agentModelOverlays[0]?.splitRanges ?? [],
        // The cases plugin closes over the bubble list, so a changed set (a
        // new case opened, one diagnosed) must recreate the chart.
        sentinelCases: sentinelCases.map((kase) => ({
          id: kase.caseId,
          status: kase.status,
          diagnosed: kase.diagnosed,
          breachMs: kase.breachMs,
        })),
        singleScaleMode,
        thresholds: thresholdsEnabled ? uniqueThresholds : [],
        thresholdsEnabled,
        thresholdSensorIds: thresholdsEnabled
          ? enhancedSensorEntries.map((entry) => entry.id)
          : [],
        tooltip: true,
        // Same closure-staleness rule as sentinel overlays: the experiment
        // plugins close over their series, so a changed preview / train
        // result must recreate the chart.
        experimentOverlays: experimentOverlays.map((overlay) => ({
          color: overlay.color,
          method: overlay.method,
          points: overlay.xs.length,
          target: overlay.targetSensorId,
          valuesDigest: sentinelOverlayValuesDigest(overlay),
        })),
        excludedPoints: excludedPoints.map((series) => ({
          points: series.xs.length,
          sensorId: series.sensorId,
        })),
        excludedPointsDigest: removedPointsDigest(excludedPoints),
        penColors,
        // Boundary + dot markers close over their arrays too. The dots
        // derive from the band overlay (already value-digested above), so
        // count + boundary is enough here.
        experimentTestBoundaryMs,
        experimentOutOfBandCount: experimentOutOfBand.xs.length,
        showTargetBounds,
        showOutOfBoundsFlags,
        showResidual,
      }),
    [
      enhancedSensorEntries,
      eventsEnabled,
      excludedPoints,
      experimentOverlays,
      experimentOutOfBand,
      experimentTestBoundaryMs,
      operationalEvents,
      penColors,
      sensorAxisMapping,
      sentinelOverlays,
      sentinelCases,
      plannedOverlays,
      agentModelOverlays,
      sentinelsEnabled,
      showOutOfBoundsFlags,
      showResidual,
      showTargetBounds,
      singleScaleMode,
      thresholdsEnabled,
      uniqueThresholds,
    ]
  )

  const plugins = useMemo(() => {
    const pluginList: uPlot.Plugin[] = []

    if (thresholdsEnabled && uniqueThresholds.length > 0) {
      pluginList.push(
        thresholdsPlugin({
          thresholds: uniqueThresholds,
          sensorAxisMapping,
          enhancedSensorEntries,
          singleScaleMode,
        })
      )
    }

    if (eventsEnabled && operationalEvents.length > 0) {
      pluginList.push(eventMarkersPlugin({ events: operationalEvents }))
    }

    if (sentinelsEnabled && sentinelOverlays.length > 0) {
      pluginList.push(
        sentinelBandsPlugin({
          overlays: sentinelOverlays,
          sensorAxisMapping,
          singleScaleMode,
        })
      )
    }

    // Planned split preview (design-time), independent of the sentinels layer —
    // it belongs to the builder, not a served sentinel.
    if (plannedOverlays.length > 0) {
      pluginList.push(
        sentinelBandsPlugin({
          overlays: plannedOverlays,
          sensorAxisMapping,
          singleScaleMode,
        })
      )
    }

    // Agent-model windows: shade the baseline (reference) span and the graded
    // span after it on the chart itself, independent of any data layer.
    if (agentModelOverlays.length > 0) {
      pluginList.push(
        sentinelBandsPlugin({
          overlays: agentModelOverlays,
          sensorAxisMapping,
          singleScaleMode,
          // Two readable lines, not a fog: the KPI pen is what the plant DID,
          // this dashed line is what the model EXPECTED at those conditions,
          // and the ribbon is only the ±1σ noise around the expectation.
          centerLine: { alpha: 0.95, widthPx: 1.6, dashPx: [7, 4] },
        })
      )
    }

    // Case bubbles ride on top of the band, gated by the same layer.
    if (sentinelsEnabled && sentinelCases.length > 0) {
      pluginList.push(
        sentinelCasesPlugin({
          cases: sentinelCases,
          sensorAxisMapping,
          singleScaleMode,
          onCaseClick: handleCaseBubbleClick,
        })
      )
    }

    // Experiment overlays are independent of the Sentinels data layer: the
    // band reuses the sentinel band plugin (same drawing), the removed
    // points get their own marker plugin. Unlike sentinels' faint center
    // dotting, the experiment's expected line draws prominent — it IS the
    // model's prediction the user trained to see.
    if (
      experimentOverlays.length > 0 &&
      (showTargetBounds || showOutOfBoundsFlags || showResidual)
    ) {
      pluginList.push(
        sentinelBandsPlugin({
          overlays: experimentOverlays,
          sensorAxisMapping,
          singleScaleMode,
          centerLine: { alpha: 0.9, widthPx: 1.5, dashPx: [8, 4] },
          // The train series is strided to a point budget, not a uniform grid.
          sampling: 'strided',
          showBand: showTargetBounds,
          showOutOfBand: showOutOfBoundsFlags,
          showResidualLane: showResidual,
        })
      )
    }

    if (excludedPoints.length > 0) {
      pluginList.push(
        excludedDataPlugin({ excluded: excludedPoints, colors: penColors })
      )
    }

    // Test-split boundary line + out-of-band dots, on top of the pens.
    if (
      experimentOverlays.length > 0 &&
      (experimentTestBoundaryMs !== null || experimentOutOfBand.xs.length > 0)
    ) {
      pluginList.push(
        experimentMarkersPlugin({
          targetSensorId: experimentOverlays[0].targetSensorId,
          testBoundaryMs: experimentTestBoundaryMs,
          outOfBandXs: experimentOutOfBand.xs,
          outOfBandValues: experimentOutOfBand.values,
          sensorAxisMapping,
          singleScaleMode,
        })
      )
    }

    pluginList.push(
      tooltipPlugin({
        onCursorMoveRef,
        onCursorLeaveRef,
      })
    )

    return pluginList
  }, [
    enhancedSensorEntries,
    eventsEnabled,
    excludedPoints,
    experimentOverlays,
    experimentOutOfBand,
    experimentTestBoundaryMs,
    operationalEvents,
    penColors,
    handleCaseBubbleClick,
    plannedOverlays,
    agentModelOverlays,
    sensorAxisMapping,
    sentinelOverlays,
    sentinelCases,
    sentinelsEnabled,
    showOutOfBoundsFlags,
    showResidual,
    showTargetBounds,
    singleScaleMode,
    thresholdsEnabled,
    uniqueThresholds,
  ])

  // How far in this chart's data can honestly be zoomed. Held in a ref, not
  // closed over: `handleZoom` must keep a stable identity (see the comment on
  // `onCursorMoveRef`) or the live uPlot instance keeps calling a stale copy.
  const minZoomSpanRef = useRef(minSpanForSampleIntervalMs())
  const chartDataTimestamps = useMemo(
    () =>
      chartData
        .map((point) => (point as { timestamp?: unknown }).timestamp)
        .filter(
          (timestamp): timestamp is number => typeof timestamp === 'number'
        ),
    [chartData]
  )
  useEffect(() => {
    minZoomSpanRef.current = minSpanForSampleIntervalMs(
      estimateSampleIntervalMs(chartDataTimestamps)
    )
  }, [chartDataTimestamps])

  const handleZoom = useMemo(() => {
    if (xAxisSensorId) return undefined

    return (min: number, max: number) => {
      // `min`/`max` are pixel-derived, so they can be inverted, zero-width, or
      // past `now`. Sanitize before committing -- getInterval would happily turn
      // a degenerate range into a valid-looking 1s request returning no points.
      // The floor comes from the cadence of the data actually on screen: a drag
      // narrower than a few samples is a request for a chart that cannot exist.
      const clamped = clampTimeRange(min, max, {
        minSpanMs: minZoomSpanRef.current,
      })
      if (!clamped) return

      const fromISO = dayjs(clamped.from).toISOString()
      const toISO = dayjs(clamped.to).toISOString()

      const timeRangeSeconds = (clamped.to - clamped.from) / 1000
      const { interval, interval_unit } =
        TimeSeriesService.getIntervalForPenCount(
          timeRangeSeconds,
          penCountRef.current
        )

      // Tell the data processor to keep the previous chartData visible while
      // the new range loads — zoom is the one action that benefits from a
      // placeholder view.
      loadTriggerRef.current = 'zoom'
      // Pin the x window to the zoomed range for the duration of the load. The
      // preserved (wider) data would otherwise drag the auto x-scale back to its
      // full extent until the fetch lands — the view rubber-banding to the old
      // window and only snapping to the zoom once data arrives. The commit
      // effect clears this pin once the loaded data matches the zoomed window.
      setTimeXAxisPreviewRange({ min: clamped.from, max: clamped.to })
      // A zoom pins an explicit window, so leave live tail. Without this the
      // committed range survives only until the next live-tail tick, which
      // re-anchors `timeRange` to "now - lookback" and silently reverts the
      // zoom (the right-drag box zoom and the pan arrows already exit).
      exitLiveTail()
      setTimeRange({
        from: fromISO,
        to: toISO,
        interval,
        intervalUnit: interval_unit,
      })
    }
  }, [
    xAxisSensorId,
    setTimeRange,
    setTimeXAxisPreviewRange,
    exitLiveTail,
    loadTriggerRef,
  ])

  // Stabilize the series reference so data-only changes (same sensor structure)
  // use setData() instead of recreating the uPlot instance.
  const prevSeriesRef = useRef<UPlotSeriesConfig[]>(uPlotDataResult.series)
  const pendingSeriesChangeLogRef = useRef<Record<string, unknown> | null>(null)
  const stableSeries = useMemo(() => {
    const prev = prevSeriesRef.current
    const next = uPlotDataResult.series

    if (prev.length !== next.length) {
      pendingSeriesChangeLogRef.current = {
        reason: 'series-count-changed',
        previousSeriesCount: prev.length,
        nextSeriesCount: next.length,
        previousSensorIds: prev.map((series) => series._sensorId ?? null),
        nextSensorIds: next.map((series) => series._sensorId ?? null),
      }
      prevSeriesRef.current = next
      return next
    }

    const structureChanges = next.flatMap((s, i) => {
      const p = prev[i]
      const changedFields: string[] = []

      if (s.label !== p.label) changedFields.push('label')
      if (s.scale !== p.scale) changedFields.push('scale')
      if (s.show !== p.show) changedFields.push('show')
      if (s.stroke !== p.stroke) changedFields.push('stroke')
      if (s._sensorId !== p._sensorId) changedFields.push('sensorId')
      if (s._isRegression !== p._isRegression) {
        changedFields.push('isRegression')
      }
      // The pen's `gaps` closes over the dropped timestamps, so a retained
      // series object would keep drawing over the data a new filter removed.
      if (s._excludedDigest !== p._excludedDigest) {
        changedFields.push('excluded')
      }
      if (s._stepped !== p._stepped) changedFields.push('stepped')

      if (changedFields.length === 0) return []

      return [
        {
          seriesIndex: i,
          sensorId: s._sensorId ?? null,
          changedFields,
        },
      ]
    })

    if (structureChanges.length > 0) {
      pendingSeriesChangeLogRef.current = {
        reason: 'series-structure-changed',
        changeCount: structureChanges.length,
        changes: structureChanges.slice(0, 6),
        truncated: structureChanges.length > 6,
        highlightOnly: structureChanges.every(({ changedFields }) =>
          changedFields.every((field) => field === 'alpha' || field === 'width')
        ),
        highlightedSensorId: effectiveHighlightedSensorId,
        secondaryHighlightedSensorIds: effectiveSecondaryHighlightedSensorIds,
        annotationProtectedSensorIds,
      }
      prevSeriesRef.current = next
      return next
    }

    pendingSeriesChangeLogRef.current = null
    return prev
  }, [
    annotationProtectedSensorIds,
    effectiveHighlightedSensorId,
    effectiveSecondaryHighlightedSensorIds,
    uPlotDataResult.series,
  ])

  useEffect(() => {
    if (!pendingSeriesChangeLogRef.current) return

    logAnnotationDebug(
      'uplot-series-structure-change',
      pendingSeriesChangeLogRef.current
    )
  }, [stableSeries])

  const stableOptionsRef = useRef<uPlot.Options | null>(null)
  const stableOptionsSignatureRef = useRef<string | null>(null)
  const stablePluginSignatureRef = useRef<string | null>(null)
  // Everything the scatter/line mode toggle, pen highlight and de-emphasis
  // change about a series lives here, NOT in the stableSeries structural diff:
  // these fields are re-asserted onto the live instance (see useUPlotChart)
  // precisely so that changing them never destroys and recreates the chart.
  const uPlotSeriesAppearance = useMemo(
    () =>
      uPlotDataResult.series.map((series) => ({
        alpha: series.alpha,
        width: series.width,
        dash: series.dash,
        pointsShow: series.points?.show,
        pointsSize: series.points?.size,
      })),
    [uPlotDataResult.series]
  )
  const { uPlotOptions, uPlotRecreateKey } = useMemo(() => {
    const opts = generateUPlotOptions({
      sensorAxisMapping,
      singleScaleMode,
      allSensorsToRender,
      enhancedSensorEntries,
      customAxisRanges,
      sensorRanges,
      derivedValues,
      xAxisSensorId: xAxisSensorId || undefined,
      customXAxisRange,
      unitMapping,
      customSensorColors,
      width,
      height,
      onZoom: handleZoom,
      plugins,
    })

    opts.series = stableSeries

    // Two different questions, deliberately keyed differently.
    //
    // `uPlotRecreateKey` answers "is this a different chart?" and must stay
    // structural: series, axes, plugins. Range *numbers* are excluded, because
    // a new window is the same chart showing different limits — it is applied
    // to the live instance with `setScale`, not by rebuilding it.
    //
    // Object retention answers the narrower "is this literally the same
    // config?", and does include the numbers: the retained object is the one
    // `useUPlotChart` reads limits from, so retaining it across a range change
    // would hand it stale limits to apply.
    const nextSignature = buildUPlotOptionsStructuralSignature(opts)
    const nextRecreateKey = `${nextSignature}|${pluginStructuralSignature}`
    const nextRetentionSignature = `${nextSignature}|${JSON.stringify(
      buildUPlotScaleRanges(opts.scales ?? {})
    )}|${theme}|${resolvedTheme}`
    const previousOptions = stableOptionsRef.current

    if (
      previousOptions &&
      stableOptionsSignatureRef.current === nextRetentionSignature &&
      stablePluginSignatureRef.current === pluginStructuralSignature
    ) {
      return {
        uPlotOptions: previousOptions,
        uPlotRecreateKey: nextRecreateKey,
      }
    }

    stableOptionsRef.current = opts
    stableOptionsSignatureRef.current = nextRetentionSignature
    stablePluginSignatureRef.current = pluginStructuralSignature

    return {
      uPlotOptions: opts,
      uPlotRecreateKey: nextRecreateKey,
    }
  }, [
    sensorAxisMapping,
    singleScaleMode,
    allSensorsToRender,
    enhancedSensorEntries,
    customAxisRanges,
    sensorRanges,
    derivedValues,
    xAxisSensorId,
    customXAxisRange,
    unitMapping,
    customSensorColors,
    width,
    height,
    handleZoom,
    pluginStructuralSignature,
    plugins,
    stableSeries,
    theme,
    resolvedTheme,
  ])

  const hasDataPoints = useMemo(() => {
    const data = uPlotDataResult.data
    if (!Array.isArray(data) || data.length < 2) return false
    const xValues = data[0]
    return Array.isArray(xValues) && xValues.length > 0
  }, [uPlotDataResult.data])

  return {
    uPlotOptions,
    uPlotRecreateKey,
    uPlotSeriesAppearance,
    uPlotData: uPlotDataResult.data,
    hasDataPoints,
  }
}
